import { useClient } from '@/context/ClientContext'
import { usePermissions } from '@/context/PermissionsContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import type { MyOrganisationInvitation } from '@droidmap/organisation-service-contract'
import type { DroidMapMetadata } from '@droidmap/shared-service-contract'
import {
  HttpResponseError,
  HttpStatus,
  serverRefusalMessage,
} from '@droidmap/shared-utils'

/** Query key for the caller's own open invitations. */
export const myInvitationsQueryKey = (userId: string) =>
  ['invitations', 'me', userId] as const

/**
 * Word a failed accept / decline for the invitee. 404 covers every way an
 * invitation can stop being actionable — expired, revoked, already answered,
 * or not theirs — so it gets one sentence; a 409 (org frozen or being
 * deleted) carries the server's own explanation.
 */
export const invitationActionErrorMessage = (
  error: unknown,
  action: 'accept' | 'decline',
): string => {
  if (
    error instanceof HttpResponseError &&
    error.status === HttpStatus.NOT_FOUND_404
  ) {
    return 'This invitation is no longer available. It may have expired or been withdrawn.'
  }
  return (
    serverRefusalMessage(error) ??
    `Couldn’t ${action} the invitation. Please try again in a moment.`
  )
}

/**
 * The caller's open invitations plus Accept / Decline (#3058).
 *
 * - Accept refreshes the `['organisations', userId]` list (the org is now
 *   theirs) and the permission grants, then navigates into the org.
 * - Decline drops the card. Either way the invitation list is refetched, so a
 *   card that turned out to be stale (404) disappears too.
 */
export function useMyInvitations(userId: string, metadata: DroidMapMetadata) {
  const { organisationClient } = useClient()
  const { refreshPermissions } = usePermissions()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [actionError, setActionError] = useState<string>()

  const query = useQuery({
    queryKey: myInvitationsQueryKey(userId),
    queryFn: async () =>
      (await organisationClient.listMyOrganisationInvitations(metadata)).items,
    enabled: Boolean(userId),
  })

  const refetchInvitations = () =>
    queryClient.invalidateQueries({ queryKey: myInvitationsQueryKey(userId) })

  const accept = useMutation({
    mutationFn: (invitation: MyOrganisationInvitation) =>
      organisationClient.acceptOrganisationInvitation(
        { invitationId: invitation.id },
        metadata,
      ),
    onMutate: () => setActionError(undefined),
    onSuccess: async (accepted) => {
      // The server has accepted: none of these refreshes may turn that into
      // a reported failure (a throw here would run `onError`) or stop the
      // navigation into the org.
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['organisations', userId] }),
        refetchInvitations(),
        refreshPermissions(),
      ])
      await navigate({ to: '/orgs/$orgId', params: { orgId: accepted.orgId } })
    },
    onError: async (error) => {
      setActionError(invitationActionErrorMessage(error, 'accept'))
      await refetchInvitations()
    },
  })

  const decline = useMutation({
    mutationFn: (invitation: MyOrganisationInvitation) =>
      organisationClient.declineOrganisationInvitation(
        { invitationId: invitation.id },
        metadata,
      ),
    onMutate: () => setActionError(undefined),
    onSuccess: async (_declined, invitation) => {
      queryClient.setQueryData<MyOrganisationInvitation[]>(
        myInvitationsQueryKey(userId),
        (current) => current?.filter((i) => i.id !== invitation.id),
      )
      await refetchInvitations()
    },
    onError: async (error) => {
      setActionError(invitationActionErrorMessage(error, 'decline'))
      await refetchInvitations()
    },
  })

  const pending = accept.isPending
    ? accept.variables && {
        invitationId: accept.variables.id,
        action: 'accept' as const,
      }
    : decline.isPending
      ? decline.variables && {
          invitationId: decline.variables.id,
          action: 'decline' as const,
        }
      : undefined

  return {
    invitations: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    accept: accept.mutate,
    decline: decline.mutate,
    pending: pending || undefined,
    actionError,
  }
}
